import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  ChangeDetectorRef,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { VoucherService, VoucherValidationRequest, VoucherValidationResponse } from '../../../../../shared/services/voucher.service';
import { CurrencyService } from '../../../../../core/services/currency.service';
import {
  getCartItemMaximumQuantity,
  getCartItemMinimumQuantity,
  getCartItemTaxEur,
  getCartItemUnitPriceHT,
  getCartTaxEur,
} from '../../../../../shared/cart-pricing.util';

@Component({
  selector: 'app-shopping-cart',
  standalone: false,
  templateUrl: './shopping-cart.component.html',
  styleUrls: ['./shopping-cart.component.scss'],
})
export class ShoppingCartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() cart: any[] = [];
  @Input() appliedVoucher: any = null;
  @Input() voucherDiscountAmount = 0;
  @Output() quantityChange = new EventEmitter<{
    action: 'increment' | 'decrement';
    item: any;
  }>();
  @Output() deleteItemEmitter: EventEmitter<string> =
    new EventEmitter<string>();

  tvaAmount = 0;
  discountAmount = 0;
  shippingCurrency: string = 'EUR';

  private currencySubscription?: Subscription;
  private cartFingerprint: string | null = null;

  // Voucher properties
  voucherCode = '';
  voucherError = '';
  applyingVoucher = false;
  private cartRevision = 0;

  constructor(
    private voucherService: VoucherService,
    public currencyService: CurrencyService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.shippingCurrency = this.currencyService.currentCurrency;
    this.currencySubscription = this.currencyService.currency$.subscribe((curr) => {
      this.shippingCurrency = curr;
      this.cdr.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['cart']) return;

    const fingerprint = this.getCartFingerprint();
    if (this.cartFingerprint !== null && fingerprint !== this.cartFingerprint) {
      this.invalidateVoucherAfterCartChange(
        !!changes['appliedVoucher']?.previousValue,
      );
    }
    this.cartFingerprint = fingerprint;
  }

  ngOnDestroy(): void {
    this.currencySubscription?.unsubscribe();
  }

  hasDownloads(): boolean {
    return this.cart.some(item => item?.selectedDownloads && item?.selectedDownloads.length > 0);
  }

  handleQuantityChange(action: 'increment' | 'decrement', item: any) {
    this.invalidateVoucherAfterCartChange();
    this.quantityChange.emit({ action, item });
  }

  getMinimalQuantity(item: any): number {
    return getCartItemMinimumQuantity(item);
  }

  deleteItem(itemId: string) {
    this.deleteItemEmitter.emit(itemId);
  }

  removeFromCart(item: any) {
    this.invalidateVoucherAfterCartChange();
    this.deleteItem(item?.id);
  }

  private invalidateVoucherAfterCartChange(hadAppliedVoucherBeforeInputChange = false): void {
    this.cartRevision += 1;
    if (!this.appliedVoucher && !this.applyingVoucher && !hadAppliedVoucherBeforeInputChange) return;

    const hadAppliedVoucher =
      !!this.appliedVoucher || hadAppliedVoucherBeforeInputChange;
    this.appliedVoucher = null;
    this.voucherDiscountAmount = 0;
    this.applyingVoucher = false;
    this.voucherError = hadAppliedVoucher
      ? 'Your basket changed. Apply the voucher again to update your discount.'
      : 'Your basket changed while checking the voucher. Apply it again.';
    if (hadAppliedVoucher) this.dispatchVoucherChange();
  }

  getValue(value: any): any {
    if (value && value.variant && typeof value.variant.label === 'string') {
      const cleanedLabel = value.variant.label.replace(/:+$/, '').trim();
      return {
        ...value,
        variant: {
          ...value.variant,
          label: cleanedLabel
        }
      };
    }
    return value;
  }

  decrementQuantity(item: any) {
    const minimalQuantity = this.getMinimalQuantity(item);
    const currentQuantity = Number(item?.quantity);
    const newQuantity = currentQuantity % minimalQuantity === 0
      ? Math.max(minimalQuantity, currentQuantity - minimalQuantity)
      : Math.max(minimalQuantity, Math.floor(currentQuantity / minimalQuantity) * minimalQuantity);

    // Only emit event if the new quantity would be valid
    if (newQuantity < currentQuantity && newQuantity >= minimalQuantity) {
      this.handleQuantityChange('decrement', item);
    }
  }

  incrementQuantity(item: any) {
    const minimumQuantity = this.getMinimalQuantity(item);
    const currentQuantity = Number(item?.quantity);
    const nextQuantity = currentQuantity < minimumQuantity
      ? minimumQuantity
      : Math.ceil(currentQuantity / minimumQuantity) * minimumQuantity +
        (currentQuantity % minimumQuantity === 0 ? minimumQuantity : 0);
    if (nextQuantity > this.getMaximumQuantity(item)) return;
    this.handleQuantityChange('increment', item);
  }

  canIncreaseQuantity(item: any): boolean {
    const minimumQuantity = this.getMinimalQuantity(item);
    const currentQuantity = Number(item?.quantity);
    const nextQuantity = currentQuantity < minimumQuantity
      ? minimumQuantity
      : currentQuantity % minimumQuantity === 0
        ? currentQuantity + minimumQuantity
        : Math.ceil(currentQuantity / minimumQuantity) * minimumQuantity;
    return Number.isInteger(currentQuantity) &&
      nextQuantity <= this.getMaximumQuantity(item);
  }

  getMaximumQuantity(item: any): number {
    return getCartItemMaximumQuantity(item);
  }

  getQuantityLimitMessage(item: any): string {
    const maximum = this.getMaximumQuantity(item);
    const minimum = this.getMinimalQuantity(item);
    return maximum < minimum
      ? `Only ${maximum} units are available; the minimum order is ${minimum}.`
      : `Maximum available quantity: ${maximum}.`;
  }

  private getCartFingerprint(): string {
    return JSON.stringify(
      this.cart.map((item) => ({
        id: item?.id,
        productId: item?.product?.id,
        quantity: item?.quantity,
        priceHT: item?.product?.priceHT,
        discountRate: item?.product?.discountRate,
        tvaRate: item?.product?.tvaRate,
        volumeDiscounts: item?.product?.volumeDiscounts,
        selectedDownloads: (item?.selectedDownloads || [])
          .map((download: any) => String(download?.id))
          .sort(),
        selectedColorId: item?.selectedColor?.id,
        variants: Object.keys(item?.variants || {})
          .sort()
          .map((key) => [key, item.variants[key]?.id, item.variants[key]?.value]),
      })),
    );
  }

  getTotal() {
    return {
      totalHt: this.cart.reduce((total, item) => {
        return total + getCartItemUnitPriceHT(item) * (Number(item?.quantity) || 0);
      }, 0),
      products: this.cart.length || 0,
      quantity: this.cart.reduce((total, item) => {
        return total + item?.quantity;
      }, 0),
    };
  }

  getCartTotal(): number {
    const productsSubtotal = this.cart.reduce(
      (total, item) =>
        total + getCartItemUnitPriceHT(item) * (Number(item?.quantity) || 0),
      0,
    );
    const downloadsTotal = this.cart.reduce(
      (total, item) => total + this.getDownloadsTotal(item),
      0,
    );
    return productsSubtotal + downloadsTotal + this.getTvaAmount();
  }

  // Method to calculate total that matches individual row totals
  getCartTotalMatchingRows(): number {
    return this.cart.reduce((total, item) => {
      const itemTotal = this.getItemTotalIncludingVAT(item);
      const downloadsTotal = this.getDownloadsTotal(item);
      return total + itemTotal + downloadsTotal;
    }, 0);
  }

  getTvaAmount(): number {
    return getCartTaxEur(this.cart || []);
  }

  getColorHex(colorName: string): string {
    if (!colorName) return '#cccccc';

    const colorMap: { [key: string]: string } = {
      'black': '#000000',
      'white': '#ffffff',
      'red': '#ef4444',
      'blue': '#3b82f6',
      'green': '#22c55e',
      'yellow': '#eab308',
      'orange': '#f97316',
      'purple': '#a855f7',
      'pink': '#ec4899',
      'gray': '#6b7280',
      'grey': '#6b7280',
      'brown': '#92400e',
      'navy': '#1e40af',
      'lime': '#84cc16',
      'cyan': '#06b6d4',
      'indigo': '#6366f1',
      'violet': '#8b5cf6',
      'rose': '#f43f5e',
      'emerald': '#10b981',
      'teal': '#14b8a6',
      'sky': '#0ea5e9',
      'amber': '#f59e0b',
      'neutral': '#737373',
      'slate': '#64748b',
      'zinc': '#71717a',
      'stone': '#78716c',
    };

    const lowerColorName = colorName.toLowerCase();
    if (colorMap[lowerColorName]) {
      return colorMap[lowerColorName];
    }

    for (const [key, value] of Object.entries(colorMap)) {
      if (lowerColorName.includes(key) || key.includes(lowerColorName)) {
        return value;
      }
    }

    let hash = 0;
    for (let i = 0; i < colorName.length; i++) {
      hash = colorName.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 60%, 50%)`;
  }

  // Helper method for template to parse float values
  parseFloat(value: any): number {
    return parseFloat(value || 0);
  }

  // Method to calculate original item total before discount
  getOriginalItemTotal(item: any): number {
    const originalPriceHT = Number(item?.originalPriceHT ?? item?.product?.priceHT);
    const quantity = item?.quantity || 0;
    return (Number.isFinite(originalPriceHT) ? originalPriceHT : getCartItemUnitPriceHT(item)) * quantity;
  }

  getItemDiscountPercent(item: any): number {
    const originalPrice = Number(item?.originalPriceHT ?? item?.product?.priceHT);
    const discountedPrice = getCartItemUnitPriceHT(item);
    if (!Number.isFinite(originalPrice) || originalPrice <= 0 || discountedPrice >= originalPrice) {
      return 0;
    }

    return Math.round((1 - discountedPrice / originalPrice) * 100);
  }

  getItemDiscountLabel(item: any): string {
    const discountPercent = this.getItemDiscountPercent(item);
    if (!discountPercent) return '';

    return item?.appliedVolumeDiscount
      ? `-${discountPercent}% volume discount`
      : `-${discountPercent}% OFF`;
  }

  // Method to calculate discounted item total (what customer actually pays)
  getDiscountedItemTotal(item: any): number {
    const finalPriceHT = getCartItemUnitPriceHT(item);
    const quantity = item?.quantity || 0;

    return finalPriceHT * quantity;
  }

  // Method to calculate item total including VAT (based on discounted price)
  getItemTotalIncludingVAT(item: any): number {
    // Use the same logic as the QTY TOTAL column + VAT column
    const qtyTotal = this.getDiscountedItemTotal(item); // €450.00
    const vatAmount = this.getItemVAT(item); // Use the corrected VAT calculation method

    return qtyTotal + vatAmount; // €450 + €45 = €495
  }

  // Voucher methods
  applyVoucher(): void {
    if (!this.voucherCode.trim()) {
      this.voucherError = 'Please enter a voucher code';
      return;
    }

    const subtotal = this.getCartTotalMatchingRows();
    const request: VoucherValidationRequest = {
      code: this.voucherCode.trim().toUpperCase(),
      totalAmount: subtotal
    };
    const cartRevision = this.cartRevision;

    this.applyingVoucher = true;
    this.voucherError = '';

    this.voucherService.validateVoucher(request).subscribe({
      next: (response: VoucherValidationResponse) => {
        if (cartRevision !== this.cartRevision) return;

        if (
          response.valid &&
          response.voucher &&
          typeof response.discountAmount === 'number' &&
          Number.isFinite(response.discountAmount) &&
          response.discountAmount >= 0
        ) {
          this.applyingVoucher = false;
          this.appliedVoucher = response.voucher;
          this.voucherDiscountAmount = response.discountAmount;
          this.voucherError = '';

          this.dispatchVoucherChange();
        } else {
          this.applyingVoucher = false;
          // Handle invalid voucher response with error message - DON'T call removeVoucher()
          this.voucherError = response.error || 'The voucher code is wrong or expired';
          // Clear voucher data but keep the error message and code for user reference
          this.appliedVoucher = null;
          this.voucherDiscountAmount = 0;
        }
      },
      error: (error: any) => {
        if (cartRevision !== this.cartRevision) return;

        this.applyingVoucher = false;

        // Provide more specific error messages
        if (error.status === 401) {
          this.voucherError = 'Your session expired. Please sign in again and retry the voucher.';
        } else if (error.status === 404) {
          this.voucherError = 'The voucher code is wrong or expired';
        } else if (error.error?.data?.[0]?.error) {
          // Handle wrapped error response from backend
          this.voucherError = error.error.data[0].error;
        } else if (error.error?.message) {
          this.voucherError = error.error.message;
        } else {
          this.voucherError = 'The voucher code is wrong or expired';
        }

        // Clear voucher data but keep the error message and code for user reference
        this.appliedVoucher = null;
        this.voucherDiscountAmount = 0;
      }
    });
  }

  removeVoucher(): void {
    this.appliedVoucher = null;
    this.voucherDiscountAmount = 0;
    this.voucherCode = '';
    this.voucherError = '';

    this.dispatchVoucherChange();
  }

  private dispatchVoucherChange(): void {
    window.dispatchEvent(new CustomEvent('voucherApplied', {
      detail: {
        discountAmount: this.voucherDiscountAmount,
        voucher: this.appliedVoucher
      }
    }));
  }

  getVoucherDisplayText(): string {
    if (!this.appliedVoucher) return '';

    if (this.appliedVoucher.discountType === 'percentage') {
      return `${this.appliedVoucher.discountValue}% OFF`;
    } else {
      return `${this.currencyService.format(this.appliedVoucher.discountValue)} OFF`;
    }
  }

  // Updated total calculation methods to include voucher discount
  getCartTotalWithVoucher(): number {
    const originalTotal = this.getCartTotalMatchingRows();
    return Math.max(0, originalTotal - this.voucherDiscountAmount);
  }

  // Clear error message when user starts typing a new voucher code
  onVoucherCodeChange(): void {
    if (this.voucherError) {
      this.voucherError = '';
    }
  }

  // Method to calculate total downloads price for an item
  getDownloadsTotal(item: any): number {
    if (!item?.selectedDownloads || item.selectedDownloads.length === 0) {
      return 0;
    }

    return item.selectedDownloads.reduce((total: number, download: any) => {
      const downloadPrice = parseFloat(download.price || 0);
      return total + downloadPrice;
    }, 0);
  }

  // Method to calculate total VAT for the entire cart
  getCartVATTotal(): number {
    return getCartTaxEur(this.cart || []);
  }

  // Method to calculate VAT for a specific item (using the final discounted price)
  getItemVAT(item: any): number {
    return getCartItemTaxEur(item);
  }

  getDeliveryTimeText(item: any): string {
    const deliveryDays = String(item?.product?.deliveryDays ?? '').trim();
    const match = deliveryDays.match(/^(\d+)(?:\s*-\s*(\d+))?/);

    if (!match) {
      return 'Delivery estimate unavailable';
    }

    const minimumDays = Number(match[1]);
    const maximumDays = Number(match[2] ?? match[1]);
    if (minimumDays <= 0 || maximumDays < minimumDays) {
      return 'Delivery estimate unavailable';
    }

    const days = minimumDays === maximumDays
      ? `${maximumDays} working day${maximumDays === 1 ? '' : 's'}`
      : `${minimumDays}-${maximumDays} working days`;
    return `Delivery in ${days}`;
  }

}
